// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-floating-promises, react-hooks/exhaustive-deps, react-hooks/immutability */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import { useHistory } from "react-router-dom";

import { Segment, Step, Icon, Message, Loader } from "semantic-ui-react";

import { useDispatch, useSelector } from "react-redux";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import { useAuth0 } from "../../../../auth/auth0";
import { useUserAuth } from "../../../../hooks/useUserAuth";

import NewEditFlight from "../NewEditFlight";
import OrthoUpload from "../OrthoUpload";
import OrthoAlignment from "../OrthoAlignment";
import AddFieldsToFlight from "../AddFieldsToFlight";
import FlightAnalysis from "../FlightAnalysis";
import VectorDataLayerUpload from "../VectorDataLayerUpload/index.tsx";
import VectorDataLayerPlotResults from "../VectorDataLayerPlotResults/index.tsx";
import VectorDataLayerFieldAssoc from "../VectorDataLayerFieldAssoc/index.tsx";
import * as flightImageActions from "../../../../redux/actions/flightImageActions";
import { ApplicationArea } from "../../../Lumber/ApplicationAreas";

//-- "info": NewEditFlight (can short circuit to vectorDataLayerUpload)
//-- "orthoUpload": OrthoUpload
//-- "alignFields": OrthoAlignment
//-- "addNewFields": AddFieldsToFlight
//-- "plotAnalysis": FlightAnalysis

//-- "vectorDataLayerUpload": VectorDataLayerUpload
//-- "vectorDataLayerFieldAssoc": VectorDataLayerFieldAssoc

const FlightProcess = ({ activeStep, mode }) => {
	const history = useHistory();
	const moduleNavigation = useModuleNavigation();
	const userAuth = useUserAuth();
	const { getTokenSilently } = useAuth0();
	const dispatch = useDispatch();
	const clientId = userAuth.currentClientId; //useSelector((state) => (state.clients ? state.clients.currentId : null));
	const entityNames = useSelector((state) => state.entityNames ?? null);

	/*
	 * States
	 */
	const [step] = useState(activeStep);
	const [flightUploadedMessage, setFlightUploadedMessage] = useState("");
	const [checkImageUploadComplete, setCheckImageUploadComplete] = useState(false);

	// boolean state for controlling wizard ribbon nav from vector uploads -> field association step
	const [vectorDataLayerUploadsComplete, setVectorDataLayerUploadsComplete] = useState(false);
	// boolean state for controlling wizard ribbon nav from field association -> plot results step
	const [fieldAssocComplete, setFieldAssocComplete] = useState(false);

	/*
	 * Effects
	 */
	useEffect(() => {
		if (step === "alignFields" || step === "addNewFields" || step === "plotAnalysis") {
			areFlightImagesUploaded();
		} else {
			setCheckImageUploadComplete(true);
		}
	}, []);

	async function areFlightImagesUploaded() {
		const accessToken = await getTokenSilently();
		dispatch(flightImageActions.isUploaded(moduleNavigation.flightId, clientId, accessToken)).then((res) => {
			if ((step === "alignFields" || step === "addNewFields") && !res.rasterUpload) {
				setFlightUploadedMessage(
					"The orthos for this flight have not yet finished processing. Please try again in a few minutes.",
				);
			} else if (step === "plotAnalysis" && !res.azureUpload) {
				setFlightUploadedMessage(
					"The orthos for this flight have not yet finished processing. Please wait while they finish before analyzing the flight.",
				);
			}

			setCheckImageUploadComplete(true);
		});
	}

	// effect for enabling the process step after user successfully navigates to Field Association
	// having 1 or more valid uploads is a requirement to navigate here.
	useEffect(() => {
		if (step === "vectorDataLayerFieldAssoc" || step === "vectorDataLayerPlotResults") {
			setVectorDataLayerUploadsComplete(true);
		}
		if (step === "vectorDataLayerPlotResults") {
			setFieldAssocComplete(true);
		}
	}, [step]);

	/*
	 * Handlers
	 */
	function removeUploadStatusMessage() {
		setFlightUploadedMessage("");
	}

	/*
	 * Derived values
	 */
	const vectorDataLayerProcess = entityNames.dataLayer;
	const vectorDataUploadStepDisabled =
		!vectorDataLayerProcess ||
		!moduleNavigation.flightId ||
		!userAuth.hasApplicationArea(ApplicationArea.DataLayerWizardCRUD, clientId);
	const vectorDataLayerFieldAssocStepDisabled =
		!vectorDataLayerProcess ||
		!vectorDataLayerUploadsComplete ||
		!moduleNavigation.flightId ||
		!userAuth.hasApplicationArea(ApplicationArea.DataLayerWizardCRUD, clientId);
	const vectorDataLayerPlotResultsStepDisabled =
		!vectorDataLayerProcess ||
		!fieldAssocComplete ||
		!moduleNavigation.flightId ||
		!(
			userAuth.hasApplicationArea(ApplicationArea.DataLayerWizardCRUD, clientId) &&
			userAuth.hasApplicationArea(ApplicationArea.DataLayerWizard, clientId)
		);

	return (
		<Segment basic id="flight-process-steps" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
			<Step.Group widths={5}>
				<Step
					link
					active={step === "info"}
					disabled={
						!userAuth.hasApplicationArea(ApplicationArea.FlightInfo, clientId) &&
						!userAuth.hasApplicationArea(ApplicationArea.FlightInfoNoDelete, clientId)
					}
					onClick={() =>
						history.push(
							moduleNavigation.createFlightLink(
								true,
								null,
								moduleNavigation.flightId && mode !== "new" ? "info" : "new",
							),
						)
					}
				>
					<Icon name="paper plane" />
					<Step.Content>
						<Step.Title>Flight Info</Step.Title>
						<Step.Description>High-level flight information</Step.Description>
					</Step.Content>
				</Step>

				{!vectorDataLayerProcess && (
					<>
						<Step
							link
							active={step === "orthoUpload"}
							disabled={
								!moduleNavigation.flightId ||
								(!userAuth.hasApplicationArea(ApplicationArea.FlightOrthoUpload, clientId) &&
									!userAuth.hasApplicationArea(ApplicationArea.FlightOrthoUploadNoDelete, clientId))
							}
							onClick={() => history.push(moduleNavigation.createFlightLink(true, null, "ortho-upload"))}
						>
							<Icon name="cloud upload" />
							<Step.Content>
								<Step.Title>Ortho Upload</Step.Title>
								<Step.Description>Upload orthomosaic aerial images</Step.Description>
							</Step.Content>
						</Step>
						<Step
							link
							active={step === "alignFields"}
							disabled={
								!moduleNavigation.flightId || !userAuth.hasApplicationArea(ApplicationArea.FlightAlignFields, clientId)
							}
							onClick={() => history.push(moduleNavigation.createFlightLink(true, null, "align-fields"))}
						>
							<Icon className="square corners" />
							<Step.Content>
								<Step.Title>Align Fields</Step.Title>
								<Step.Description>Make sure known fields are placed properly on the ortho</Step.Description>
							</Step.Content>
						</Step>

						<Step
							link
							active={step === "addNewFields"}
							disabled={
								!moduleNavigation.flightId || !userAuth.hasApplicationArea(ApplicationArea.FlightAddNewFields, clientId)
							}
							onClick={() => history.push(moduleNavigation.createFlightLink(true, null, "add-new-fields"))}
						>
							<Icon className="plus" />
							<Step.Content>
								<Step.Title>Add New Fields</Step.Title>
								<Step.Description>Identify additional fields in the ortho</Step.Description>
							</Step.Content>
						</Step>

						<Step
							link
							active={step === "plotAnalysis"}
							disabled={
								!moduleNavigation.flightId ||
								(!userAuth.hasApplicationArea(ApplicationArea.FlightPlotAnalysis, clientId) &&
									!userAuth.hasApplicationArea(ApplicationArea.FlightPlotAnalysisCRUD, clientId))
							}
							onClick={() => history.push(moduleNavigation.createFlightLink(true, null, "plot-analysis"))}
						>
							<Icon className="chart bar outline" />
							<Step.Content>
								<Step.Title>Plot Analysis</Step.Title>
								<Step.Description>Analyze plots and view results</Step.Description>
							</Step.Content>
						</Step>
					</>
				)}

				{vectorDataLayerProcess && (
					<>
						<Step
							link
							active={step === "vectorDataLayerUpload"}
							disabled={vectorDataUploadStepDisabled}
							onClick={() => history.push(moduleNavigation.createFlightLink(true, null, "data-layer-upload"))}
						>
							<Icon name="cloud upload" />
							<Step.Content>
								<Step.Title>Data Layer Upload</Step.Title>
								<Step.Description>Upload and configure data layer uploads</Step.Description>
							</Step.Content>
						</Step>

						<Step
							link
							active={step === "vectorDataLayerFieldAssoc"}
							disabled={vectorDataLayerFieldAssocStepDisabled}
							onClick={() => history.push(moduleNavigation.createFlightLink(true, null, "data-layer-assoc-fields"))}
						>
							<Icon name="map" />
							<Step.Content>
								<Step.Title>Field Association</Step.Title>
								<Step.Description>Link data layers to fields</Step.Description>
							</Step.Content>
						</Step>

						<Step
							link
							active={step === "vectorDataLayerPlotResults"}
							disabled={vectorDataLayerPlotResultsStepDisabled}
							onClick={() => history.push(moduleNavigation.createFlightLink(true, null, "data-layer-plot-results"))}
						>
							<Icon name="parcels" />
							<Step.Content>
								<Step.Title>Plot Results</Step.Title>
								<Step.Description>Associate plots based on what zones they fall in</Step.Description>
							</Step.Content>
						</Step>
					</>
				)}
			</Step.Group>
			<>
				{flightUploadedMessage ? (
					<Message warning content={flightUploadedMessage} style={{ marginTop: "unset" }} />
				) : null}
				<Segment
					basic
					id="flight-process-step-content"
					style={{
						marginTop: "unset",
						paddingBottom: "unset",
						minHeight: 70,
						height: 0,
						flexGrow: 1,
						overflow: step === "alignFields" || step === "addNewFields" ? "hidden" : "auto",
					}}
				>
					{(() => {
						switch (step) {
							case "info":
								return <NewEditFlight mode={moduleNavigation.flightId && mode !== "new" ? "edit" : "new"} />;
							case "orthoUpload":
								return <OrthoUpload />;
							case "alignFields":
								return checkImageUploadComplete && entityNames?.flightId === moduleNavigation.flightId ? (
									<OrthoAlignment />
								) : (
									<Loader active>Verifying Upload Status</Loader>
								);
							case "addNewFields":
								return checkImageUploadComplete && entityNames?.flightId === moduleNavigation.flightId ? (
									<AddFieldsToFlight />
								) : (
									<Loader active>Verifying Upload Status</Loader>
								);
							case "plotAnalysis":
								return <FlightAnalysis removeUploadStatusMessage={removeUploadStatusMessage} />;
							case "vectorDataLayerUpload":
								return (
									<VectorDataLayerUpload
										uploadsComplete={vectorDataLayerUploadsComplete}
										onUploadsComplete={setVectorDataLayerUploadsComplete}
									/>
								);
							case "vectorDataLayerFieldAssoc":
								return (
									<VectorDataLayerFieldAssoc
										fieldAssocComplete={fieldAssocComplete}
										onFieldAssocComplete={setFieldAssocComplete}
									/>
								);
							case "vectorDataLayerPlotResults":
								return <VectorDataLayerPlotResults />;
							default:
								return (
									<Message negative header="Error: Unknown Step" content={`The step "${step}" is not recognized.`} />
								);
						}
					})()}
				</Segment>
			</>
		</Segment>
	);
};

FlightProcess.propTypes = {
	activeStep: PropTypes.string.isRequired,
	mode: PropTypes.string.isRequired,
};

export default FlightProcess;
